import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mail, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISSED_AT_KEY = "infocity_email_verify_dismissed_at";
const SNOOZE_DAYS = 1;
const SHOW_DELAY_MS = 1500;

// Global "Modo SOFT" reminder — NEVER blocks navigation or app usage. Only
// shown when the backend explicitly says email_verified===false (existing
// pre-migration accounts don't have this field at all, so they never see it).
export function EmailVerificationPrompt() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!user || user.email_verified !== false) { setVisible(false); return; }
    const dismissedAt = Number(localStorage.getItem(DISMISSED_AT_KEY) || 0);
    const daysSince = (Date.now() - dismissedAt) / 86400000;
    if (dismissedAt && daysSince < SNOOZE_DAYS) return;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [user]);

  if (!visible) return null;

  const dismiss = () => { localStorage.setItem(DISMISSED_AT_KEY, String(Date.now())); setVisible(false); };

  const resend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, { headers: getAuthHeaders() });
      toast.success(t("emailVerify.resendSuccess"));
    } catch {
      toast.error(t("errors.unknownError"));
    } finally {
      setResending(false);
    }
  };

  return (
    <div data-testid="email-verification-prompt"
      className="fixed bottom-36 sm:bottom-24 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-[64] p-4 bg-white border border-slate-200 rounded-2xl shadow-[0_10px_36px_rgba(15,23,42,0.16)]"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-[#028CF8]/10 flex items-center justify-center flex-shrink-0">
          <Mail className="h-4.5 w-4.5 text-[#028CF8]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900">{t("emailVerify.title")}</p>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{t("emailVerify.body")}</p>
          <div className="flex items-center gap-2 mt-3">
            <button data-testid="email-verification-resend-button" onClick={resend} disabled={resending}
              className="h-9 px-3.5 rounded-lg bg-[#028CF8] text-white text-xs font-semibold disabled:opacity-50 flex items-center gap-1.5">
              {resending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t("emailVerify.resend")}
            </button>
            <button data-testid="email-verification-dismiss-button" onClick={dismiss}
              className="h-9 px-3.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-50 transition-colors">
              {t("emailVerify.dismiss")}
            </button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="close" data-testid="email-verification-close" className="text-slate-400 hover:text-slate-600 flex-shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
